// The main message-area pane: header, message list, composer.
//
// Renders live channel messages, file uploads, profiles, and the composer.

import {
  Bell,
  FileCode,
  Files,
  Gauge,
  Hash,
  Lock,
  Pin,
  Plus,
  Search,
  Send,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  GlassMaterial,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@link/ui";

import {
  DEFAULT_COMMUNITY_PERMISSIONS,
  ROLE_PERMISSIONS,
  SECRETS_DEFAULT_EXPIRATION,
  type SecretsExpiration,
} from "@link/shared";

import type { ChannelView, MessageView, SpaceView } from "../types/ui";
import { api, type SpaceEmoji, type SpaceMember, type SpaceRole } from "../lib/api";
import { BADGES, type BadgeId } from "../lib/badges";
import { renderForSurface } from "../lib/secrets-placeholder";
import { getSelfFileRecipient } from "../lib/upload";
import { useMessages } from "../state/data";
import { useSelf } from "../state/SelfSession";
import { useUploads } from "../state/Uploads";
import { useRealtimeSubscription } from "../state/Realtime";
import { ComposerActions } from "./messages/ComposerActions";
import {
  ChatFileDropOverlay,
  useChatFileDrop,
} from "./messages/ChatFileDropZone";
import { MessageItem } from "./messages/MessageItem";
import { ComposerReplyPreview } from "./messages/ReplyPreview";
import type { MemberProfile } from "./MemberProfilePopover";
import { groupsWithPrevious } from "../lib/message-presentation";
import { useComposerAutoGrow, useComposerDraft, useComposerInsert } from "../lib/composer";
import { beginUpdateActivity } from "../lib/update-activity";
import { highestMemberRole } from "../lib/member-presentation";

interface Props {
  space: SpaceView;
  channel: ChannelView | undefined;
  /** Whether the right-hand member list is visible. */
  showMembers: boolean;
  /** Toggle handler for the Members button. */
  onToggleMembers: () => void;
}

type MentionSuggestion =
  | {
      kind: "special";
      value: "everyone" | "here";
      label: string;
      description: string;
    }
  | {
      kind: "member";
      value: string;
      member: SpaceMember;
    };

const CHANNEL_ICON: Record<ChannelView["channelType"], typeof Hash> = {
  overview: Gauge,
  text: Hash,
  snippets: FileCode,
  files: Files,
  secrets: Lock,
};

export function MainPane({
  space,
  channel,
  showMembers,
  onToggleMembers,
}: Props) {
  const [quickEdit, setQuickEdit] = useState<MessageView | null>(null);
  const [replyTo, setReplyTo] = useState<MessageView | null>(null);
  const [secretsExpiration, setSecretsExpiration] = useState<SecretsExpiration>(
    SECRETS_DEFAULT_EXPIRATION,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [roles, setRoles] = useState<SpaceRole[]>([]);
  const [defaultPermissions, setDefaultPermissions] = useState(DEFAULT_COMMUNITY_PERMISSIONS);
  const [spaceEmojis, setSpaceEmojis] = useState<SpaceEmoji[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionMenuDismissed, setMentionMenuDismissed] = useState(false);
  const [notificationMode, setNotificationMode] = useState("Use Space default");
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const composerInput = useRef<HTMLTextAreaElement | null>(null);
  const presentationRequest = useRef(0);
  const self = useSelf();
  const uploads = useUploads();
  const draftStorageKey = channel === undefined
    ? null
    : `link:composer-draft:${self.identity.accountXid}:channel:${channel.xid}`;
  const [draft, setDraft] = useComposerDraft(draftStorageKey, quickEdit !== null);
  useComposerAutoGrow(composerInput, draft);
  const insertIntoDraft = useComposerInsert(composerInput, draft, setDraft);
  useEffect(() => {
    setQuickEdit(null);
    setReplyTo(null);
  }, [draftStorageKey]);
  useEffect(() => {
    if (channel === undefined) return;
    const key = `link:channel-notifications:${self.identity.accountXid}:${channel.xid}`;
    setNotificationMode(window.localStorage.getItem(key) ?? "Use Space default");
  }, [channel, self.identity.accountXid]);
  // Live messages — the hook talks to the API after authentication. The encryption kind
  // is derived from the channel + space type so E2E channels
  // (Personal Workspace, Secrets) encrypt outgoing bodies before
  // they hit the wire.
  const encryptionKind: "atrest" | "e2e-self" | "e2e-secret" =
    space.spaceType === "personal_workspace"
      ? "e2e-self"
      : channel?.channelType === "secrets"
        ? "e2e-secret"
        : "atrest";
  const liveMessages = useMessages(
    channel?.xid ?? null,
    [],
    encryptionKind,
    [],
    self.identity.accountXid,
    self.identity.name,
    self.identity.username,
  );
  const messageState = liveMessages.messages;
  const memberNameRoles = useMemo(() => new Map(members.map(member => [
    member.account_id, highestMemberRole(member, roles),
  ])), [members, roles]);
  const onReactLive = liveMessages.toggleReact;
  const onTogglePinLive = liveMessages.togglePin;

  const effectivePermissions = useMemo(() => {
    if (space.ownerAccountXid === self.identity.accountXid) return Number.MAX_SAFE_INTEGER;
    const member = members.find((candidate) => candidate.account_id === self.identity.accountXid);
    if (member?.is_admin === true) return Number.MAX_SAFE_INTEGER;
    const assigned = new Set(member?.roles.map((role) => role.xid) ?? []);
    return roles.reduce(
      (mask, role) => assigned.has(role.xid) ? mask | role.permissions : mask,
      defaultPermissions,
    );
  }, [defaultPermissions, members, roles, self.identity.accountXid, space.ownerAccountXid]);
  const canReact = (effectivePermissions & ROLE_PERMISSIONS.add_reactions) !== 0;
  const canPin = (effectivePermissions & ROLE_PERMISSIONS.pin_messages) !== 0;
  const canModerateMessages = (effectivePermissions & ROLE_PERMISSIONS.manage_messages) !== 0;
  const canMentionEveryone = (effectivePermissions & ROLE_PERMISSIONS.mention_everyone) !== 0;

  useEffect(() => {
    const mention = (event: Event) => {
      const detail = (event as CustomEvent<{spaceXid?: string; username?: string}>).detail;
      if (detail?.spaceXid !== space.xid || channel === undefined || typeof detail.username !== "string"
        || !members.some((member) => member.username === detail.username)) return;
      setDraft((current) => `${current}${current.length > 0 && !/\s$/.test(current) ? " " : ""}@${detail.username} `);
      setMentionMenuDismissed(true);
      composerInput.current?.focus();
    };
    window.addEventListener("link:mention-member", mention);
    return () => window.removeEventListener("link:mention-member", mention);
  }, [space.xid, channel, members]);

  const refreshMemberPresentation = useCallback(() => {
    const request = ++presentationRequest.current;
    void Promise.all([api.spaces.members(space.xid), space.spaceType === "community" ? api.spaces.roles(space.xid) : Promise.resolve({roles: [] as SpaceRole[], default_permissions: 0})])
      .then(([memberResult, roleResult]) => {
        if (request !== presentationRequest.current) return;
        setMembers(memberResult.members); setRoles(roleResult.roles); setDefaultPermissions(roleResult.default_permissions);
      }).catch(() => undefined);
  }, [space.xid, space.spaceType]);
  useEffect(() => {
    setMembers([]); setRoles([]); setDefaultPermissions(0);
    refreshMemberPresentation();
    return () => { presentationRequest.current += 1; };
  }, [refreshMemberPresentation]);
  useRealtimeSubscription([`space:${space.xid}`], (frame) => {
    if (frame.type === "profile.updated" || frame.type.startsWith("space.member") || frame.type.startsWith("space.role") || frame.type === "community_tag.updated") refreshMemberPresentation();
  });
  useEffect(() => {
    const refresh = () => { if (!document.hidden) refreshMemberPresentation(); };
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.clearInterval(interval); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [refreshMemberPresentation]);

  useEffect(() => {
    let cancelled = false;
    void api.spaces.emojis(space.xid).then((result) => { if (!cancelled) setSpaceEmojis(result.emojis); }).catch(() => { if (!cancelled) setSpaceEmojis([]); });
    return () => { cancelled = true; };
  }, [space.xid]);

  const mentionMatch = encryptionKind === "atrest" && !mentionMenuDismissed
    ? draft.match(/(?:^|[\s([{])@([a-z0-9_]*)$/i)
    : null;
  const mentionSuggestions = useMemo(() => {
    if (mentionMatch === null) return [];
    const query = (mentionMatch[1] ?? "").toLowerCase();
    const specialOptions: MentionSuggestion[] = [
      {
        kind: "special",
        value: "everyone",
        label: "@everyone",
        description: "Notify everyone who can view this channel",
      },
      {
        kind: "special",
        value: "here",
        label: "@here",
        description: "Notify everyone currently online",
      },
    ];
    const special = canMentionEveryone
      ? specialOptions.filter((suggestion) => suggestion.value.includes(query))
      : [];
    const memberSuggestions: MentionSuggestion[] = members
      .filter((member) => {
        const display = (member.nickname ?? member.display_name ?? "").toLowerCase();
        return member.username.toLowerCase().includes(query) || display.includes(query);
      })
      .map((member) => ({ kind: "member" as const, value: member.username, member }));
    return [...special, ...memberSuggestions].slice(0, 10);
  }, [canMentionEveryone, members, mentionMatch]);

  const insertMention = (suggestion: MentionSuggestion): void => {
    setDraft((current) => current.replace(/@([a-z0-9_]*)$/i, `@${suggestion.value} `));
    setMentionIndex(0);
    setMentionMenuDismissed(false);
  };

  const sendDraft = (): void => {
    const body = draft.trim();
    if (body.length === 0) return;
    if (quickEdit !== null) {
      void liveMessages.edit(quickEdit.xid, body).then(() => {
        setDraft("");
        setQuickEdit(null);
      });
      return;
    }
    const mentions = encryptionKind === "atrest" ? mentionedAccounts(body, members) : [];
    void liveMessages.send(body, mentions, replyTo?.xid);
    setDraft("");
    setReplyTo(null);
    setMentionIndex(0);
    setMentionMenuDismissed(false);
  };

  const onReact = (m: MessageView, emoji: string): void => {
    void onReactLive(m.xid, emoji);
  };

  const onTogglePin = (m: MessageView): void => {
    void onTogglePinLive(m.xid);
  };

  const queueFiles = (files: File[]): void => {
    if (channel === undefined || liveMessages.status !== "ready") {
      setUploadError("This channel is still loading. Try the upload again in a moment.");
      return;
    }
    setUploadError(null);
    const finishUpload = beginUpdateActivity();
    void (async () => {
      try {
        const e2eRecipients =
          encryptionKind === "atrest"
            ? undefined
            : [await getSelfFileRecipient(self.identity.accountXid)];
        for (const file of files) {
          const result = await uploads.start({
            file,
            channelXid: channel.xid,
            e2eRecipients,
          });
          await liveMessages.sendFile({
            attachmentXid: result.attachmentXid,
            filename: result.filename,
            mime: result.mime,
            byteSize: result.byteSize,
          });
        }
      } catch (cause) {
        setUploadError(cause instanceof Error ? cause.message : "Upload failed.");
      } finally { finishUpload(); }
    })();
  };
  const fileDrop = useChatFileDrop({
    onFiles: queueFiles,
    disabled: channel === undefined || liveMessages.status !== "ready",
  });

  if (channel === undefined) {
    return (
      <main className="flex flex-1 items-center justify-center">
        <p className="text-sm text-cloud-500">No channel selected.</p>
      </main>
    );
  }

  const Icon = CHANNEL_ICON[channel.channelType];
  const isSecrets = channel.channelType === "secrets";
  const isFiles = channel.channelType === "files";

  // Group messages by date for the day-divider, then order the
  // groups oldest-first so the chronological flow inside a day
  // pairs with the chronological flow across days.
  const grouped = new Map<string, MessageView[]>();
  for (const m of messageState) {
    const list = grouped.get(m.date) ?? [];
    list.push(m);
    grouped.set(m.date, list);
  }
  const orderedGroups = Array.from(grouped.entries()).sort(
    ([a], [b]) => new Date(a).getTime() - new Date(b).getTime(),
  );

  return (
    <main {...fileDrop.handlers} className="glass-content relative flex flex-1 flex-col bg-white">
      <ChatFileDropOverlay
        active={fileDrop.active}
        disabled={liveMessages.status !== "ready"}
        destination={`#${channel.name}`}
      />
      <header className="glass-toolbar liquid-lens-host relative flex h-12 items-center gap-3 border-b border-cloud-200 bg-white px-4">
        <GlassMaterial />
        <Icon className="h-5 w-5 text-cloud-500" />
        <span className="font-semibold text-cloud-800">#{channel.name}</span>
        <div className="hidden h-6 w-px bg-cloud-200 sm:block" />
        <span className="hidden text-xs text-cloud-500 sm:block">
          {space.name}
        </span>
        <div className="ml-auto flex items-center gap-2 text-cloud-500">
          <Popover>
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-strawberry-50 hover:text-strawberry-600"
                  >
                    <Pin className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent>Pinned messages</TooltipContent>
            </Tooltip>
            <PopoverContent
              align="end"
              className="w-[min(420px,calc(100vw-2rem))] p-0"
              side="bottom"
            >
              <div className="border-b border-cloud-200 px-4 py-3">
                <p className="text-sm font-semibold text-cloud-800">
                  Pinned messages
                </p>
                <p className="text-[11px] text-cloud-500">
                  Newest pin first · this channel
                </p>
              </div>
              {(() => {
                const pinned = messageState.filter(
                  (m) => m.pinned === true,
                );
                if (pinned.length === 0) {
                  return (
                    <div className="px-4 py-6 text-center text-xs text-cloud-500">
                      Nothing pinned yet. Right-click a message and choose
                      Pin to surface it here.
                    </div>
                  );
                }
                return (
                  <ScrollArea className="max-h-80">
                    <ul className="divide-y divide-cloud-200">
                      {pinned.map((m) => (
                        <li
                          key={m.xid}
                          className="px-4 py-2 hover:bg-cloud-50"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-cloud-800">
                              {m.authorName}
                            </span>
                            <span className="text-[10px] text-cloud-400">
                              {m.timestamp}
                            </span>
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-xs text-cloud-700">
                            {
                              renderForSurface({
                                kind: m.kind ?? "text",
                                surface: "notes",
                                body: m.body,
                              }).display
                            }
                          </p>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                );
              })()}
            </PopoverContent>
          </Popover>

          <Popover>
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-strawberry-50 hover:text-strawberry-600"
                  >
                    <Bell className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent>Notifications</TooltipContent>
            </Tooltip>
            <PopoverContent align="end" className="w-72 p-0" side="bottom">
              <div className="border-b border-strawberry-100 px-4 py-3">
                <p className="text-sm font-semibold text-cloud-700">
                  Notification settings
                </p>
                <p className="text-[11px] text-cloud-400">
                  Just for #{channel.name}
                </p>
              </div>
              <ul className="py-1 text-sm">
                {[
                  "Use Space default",
                  "All messages",
                  "Only @mentions",
                  "Nothing",
                ].map((label) => (
                  <li key={label}>
                    <button
                      type="button"
                      onClick={() => {
                        setNotificationMode(label);
                        window.localStorage.setItem(`link:channel-notifications:${self.identity.accountXid}:${channel.xid}`, label);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-cloud-700 hover:bg-strawberry-50"
                    >
                      <span
                        className={cn(
                          "flex h-3.5 w-3.5 items-center justify-center rounded-full border-2",
                          notificationMode === label
                            ? "border-strawberry-500 bg-strawberry-500"
                            : "border-cloud-300",
                        )}
                      >
                        {notificationMode === label && (
                          <span className="h-1 w-1 rounded-full bg-white" />
                        )}
                      </span>
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onToggleMembers}
                aria-label={showMembers ? "Hide member list" : "Show member list"}
                aria-pressed={showMembers}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
                  showMembers
                    ? "bg-strawberry-100 text-strawberry-700"
                    : "hover:bg-strawberry-50 hover:text-strawberry-600",
                )}
              >
                <Users className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {showMembers ? "Hide member list" : "Show member list"}
            </TooltipContent>
          </Tooltip>
          <div className="hidden md:block">
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(
                  new KeyboardEvent("keydown", { key: "k", ctrlKey: true }),
                )
              }
              className="flex h-8 items-center gap-1.5 rounded-md border border-cloud-200 bg-white px-2 text-cloud-500 hover:bg-cloud-50 hover:text-strawberry-600"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="text-xs text-cloud-400">Search</span>
              <kbd className="ml-1 rounded border border-cloud-200 bg-cloud-50 px-1 text-[10px] text-cloud-500">
                ⌘K
              </kbd>
            </button>
          </div>
        </div>
      </header>

      {isSecrets && (
        <div className="flex items-center gap-2 border-b border-strawberry-200 bg-strawberry-50/80 px-4 py-2 text-xs text-strawberry-700">
          <Lock className="h-3.5 w-3.5" />
          <strong>Secrets channel.</strong>
          <span className="text-strawberry-700/80">
            Each message is encrypted on this device. Tap or click to
            reveal; default expiration is 30 days.
          </span>
        </div>
      )}

      <ScrollArea className="flex-1 px-4">
        <div data-message-list className="space-y-6 py-4">
          {orderedGroups.map(([date, messages]) => (
            <section key={date}>
              <div className="relative my-3 flex items-center justify-center">
                <div className="absolute inset-x-0 top-1/2 h-px bg-cloud-200" />
                <span className="relative rounded-full border border-strawberry-200 bg-white px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-strawberry-700 shadow-sm">
                  {formatDayLabel(date)}
                </span>
              </div>
              <ul>
                {messages.map((m, index) => (
                  <MessageItem
                    key={m.xid}
                    nameRole={memberNameRoles.get(m.authorXid === "me" ? self.identity.accountXid : m.authorXid)}
                    replyNameRole={memberNameRoles.get(m.replyTo?.authorXid ?? "")}
                    grouped={!isSecrets && groupsWithPrevious(m, messages[index - 1])}
                    message={
                      isSecrets
                        ? { ...m, kind: "secret" }
                        : channel.channelType === "snippets"
                          ? {
                              ...m,
                              kind: "snippet",
                              language: m.language ?? "typescript",
                            }
                          : channel.channelType === "files" && m.file !== undefined
                            ? { ...m, kind: "file", file: m.file }
                            : m
                    }
                    asMember={(msg) => authorAsMember(msg, space, members)}
                    resolveMention={(username) => {
                      const target = members.find((member) => member.username.toLowerCase() === username.toLowerCase());
                      return target === undefined ? undefined : memberAsProfile(target, space);
                    }}
                    onReact={canReact ? onReact : undefined}
                    onTogglePin={canPin ? onTogglePin : undefined}
                    onReply={setReplyTo}
                    onEdit={(message, body) => liveMessages.edit(message.xid, body)}
                    canModerateMessages={canModerateMessages}
                    onDelete={async (message) => {
                      if (window.confirm("Delete this message?")) await liveMessages.delete(message.xid);
                    }}
                    customEmojis={spaceEmojis.map((emoji) => ({ value: `custom:${emoji.xid}:${emoji.name}`, label: emoji.name, imageUrl: emoji.image_url }))}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </ScrollArea>

      <div className="px-4 pb-4">
        <div
          className={cn(
            "liquid-lens-host relative rounded-2xl border bg-white px-3 py-2 shadow-sm",
            isSecrets ? "border-strawberry-200" : "border-cloud-200",
          )}
        >
          <GlassMaterial radius={16} />
          {replyTo !== null && <ComposerReplyPreview message={isSecrets ? { ...replyTo, kind: "secret" } : replyTo} onCancel={() => setReplyTo(null)} />}
          {quickEdit !== null ? <div className="mb-2 flex items-center gap-2 rounded-xl bg-violet-50 px-3 py-2 text-xs text-violet-700"><span className="min-w-0 flex-1 truncate">Editing your message</span><button type="button" onClick={() => { setQuickEdit(null); setDraft(""); }} className="rounded-md p-1 hover:bg-violet-100" aria-label="Cancel edit">×</button></div> : null}
          {isSecrets && (
            <div className="mb-2 flex items-center gap-2 border-b border-strawberry-100 px-1 pb-1.5">
              <Lock className="h-3.5 w-3.5 text-strawberry-600" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-strawberry-700">
                Secret message
              </span>
              <span className="text-[11px] text-cloud-500">·</span>
              <label className="text-[11px] text-cloud-500">
                Expires in{" "}
                <select
                  value={secretsExpiration}
                  onChange={(e) =>
                    setSecretsExpiration(e.target.value as SecretsExpiration)
                  }
                  className="rounded border border-strawberry-200 bg-white px-1.5 py-0.5 text-[11px] text-strawberry-700 focus:outline-none focus:ring-1 focus:ring-strawberry-300"
                >
                  <option value="24h">24 hours</option>
                  <option value="7d">7 days</option>
                  <option value="30d">30 days (default)</option>
                  <option value="never">Never</option>
                </select>
              </label>
              <span className="ml-auto text-[10px] text-cloud-500">
                Encrypted on this device. The server stores ciphertext only.
              </span>
            </div>
          )}
          <div className="relative flex items-end gap-2">
            {mentionSuggestions.length > 0 ? (
              <div id="server-mention-suggestions" role="listbox" className="absolute bottom-[calc(100%+8px)] left-10 z-30 w-[min(420px,calc(100vw-5rem))] overflow-hidden rounded-2xl border border-cloud-300 bg-white p-1.5 shadow-2xl shadow-black/20">
                <p className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-cloud-400">Members and mentions</p>
                {mentionSuggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.kind === "special" ? suggestion.value : suggestion.member.account_id}
                    id={`server-mention-option-${index}`}
                    role="option"
                    aria-selected={index === mentionIndex}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setMentionIndex(index)}
                    onClick={() => insertMention(suggestion)}
                    className={cn("flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left", index === mentionIndex ? "bg-violet-100 text-cloud-900" : "text-cloud-700 hover:bg-cloud-50")}
                  >
                    {suggestion.kind === "member" ? (
                      <>
                        <Avatar className="h-8 w-8">{suggestion.member.avatar_url != null ? <AvatarImage src={suggestion.member.avatar_url} alt="" /> : null}<AvatarFallback>{(suggestion.member.nickname ?? suggestion.member.display_name ?? suggestion.member.username).slice(0, 1)}</AvatarFallback></Avatar>
                        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{suggestion.member.nickname ?? suggestion.member.display_name ?? suggestion.member.username}</span><span className="block truncate text-xs text-cloud-400">@{suggestion.member.username}</span></span>
                      </>
                    ) : (
                      <>
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-black text-violet-700">@</span>
                        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-violet-700">{suggestion.label}</span><span className="block truncate text-xs text-cloud-400">{suggestion.description}</span></span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => uploadInput.current?.click()}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-strawberry-50 text-strawberry-600 hover:bg-strawberry-100"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Upload</TooltipContent>
            </Tooltip>
            <input ref={uploadInput} type="file" multiple className="hidden" onChange={(event) => { const files = Array.from(event.target.files ?? []); if (files.length > 0) queueFiles(files); event.target.value = ""; }} />

            <div className="relative min-w-0 flex-1">
              <textarea
                ref={composerInput}
                value={draft}
                aria-autocomplete="list"
                aria-controls={mentionSuggestions.length > 0 ? "server-mention-suggestions" : undefined}
                aria-expanded={mentionSuggestions.length > 0}
                aria-activedescendant={mentionSuggestions.length > 0 ? `server-mention-option-${mentionIndex}` : undefined}
                onChange={(e) => { setDraft(e.target.value); setMentionIndex(0); setMentionMenuDismissed(false); }}
                onKeyDown={(event) => {
                if (event.nativeEvent.isComposing) return;
                if (mentionSuggestions.length > 0 && event.key === "ArrowDown") {
                  event.preventDefault();
                  setMentionIndex((current) => (current + 1) % mentionSuggestions.length);
                  return;
                }
                if (mentionSuggestions.length > 0 && event.key === "ArrowUp") {
                  event.preventDefault();
                  setMentionIndex((current) => (current - 1 + mentionSuggestions.length) % mentionSuggestions.length);
                  return;
                }
                if (mentionSuggestions.length > 0 && (event.key === "Tab" || (event.key === "Enter" && !event.shiftKey))) {
                  event.preventDefault();
                  const selected = mentionSuggestions[mentionIndex];
                  if (selected !== undefined) insertMention(selected);
                  return;
                }
                if (mentionSuggestions.length > 0 && event.key === "Escape") {
                  event.preventDefault();
                  setMentionMenuDismissed(true);
                  return;
                }
                if (event.key === "ArrowUp" && draft.length === 0) {
                  const latestOwn = [...messageState].reverse().find((message) => message.authorXid === self.identity.accountXid || message.authorXid === "me");
                  if (latestOwn !== undefined) {
                    event.preventDefault();
                    setQuickEdit(latestOwn);
                    setDraft(latestOwn.body);
                    return;
                  }
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendDraft();
                }
              }}
                placeholder={
                  isFiles
                    ? `Drop a file or describe the upload`
                    : isSecrets
                      ? `Type a secret`
                      : `Message #${channel.name}`
                }
                rows={1}
                className="relative z-10 min-h-9 max-h-40 w-full resize-none whitespace-pre-wrap break-words border-none bg-transparent py-2 text-sm leading-5 text-cloud-800 outline-none placeholder:text-cloud-400"
              />
            </div>

            <ComposerActions
              onInsert={insertIntoDraft}
              onAttach={queueFiles}
            />

            <Button
              size="icon"
              className="h-9 w-9 rounded-full"
              disabled={draft.trim().length === 0}
              onClick={() => {
                sendDraft();
              }}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className={cn("mt-1 px-2 text-[10px]", uploadError === null ? "text-cloud-400" : "text-red-600")}>
          {uploadError ?? "Enter to send · Shift+Enter for a new line · drag files anywhere in the chat to upload"}
        </p>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Day-divider label
// ---------------------------------------------------------------------------

/**
 * Render a friendly day label for the message-list divider:
 *
 *   - "Today"               for messages dated today
 *   - "Yesterday"           for messages dated yesterday
 *   - "Friday, Sep 19"      for dates within the last 7 days
 *   - "September 19, 2025"  otherwise (full date)
 *
 * Falls back to the input string if it doesn't parse.
 */
function formatDayLabel(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const today = startOfDay(new Date());
  const target = startOfDay(parsed);
  const diffDays = Math.round(
    (today.getTime() - target.getTime()) / (24 * 60 * 60 * 1000),
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) {
    return parsed.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// ---------------------------------------------------------------------------
// Build a `MemberProfilePopover` member shape from the message author and
// the live member store.
// ---------------------------------------------------------------------------

function authorAsMember(message: MessageView, space: SpaceView, members: SpaceMember[]): MemberProfile {
  const member = members.find((item) => item.account_id === message.authorXid);
  if (member !== undefined) return memberAsProfile(member, space);
  return {
    name: message.authorName,
    tag: message.authorXid,
    avatarUrl: message.authorAvatarUrl,
    status: "offline",
    bio: null,
    roles: [],
    mutualSpaces: [space.name],
  };
}

function memberAsProfile(member: SpaceMember, space: SpaceView, avatarOverride?: string | null): MemberProfile {
  return {
    name: member.nickname ?? member.display_name ?? member.username,
    tag: member.account_id,
    username: member.username,
    pronouns: member.pronouns,
    avatarUrl: avatarOverride ?? member.avatar_url,
    bannerColor: member.banner_color,
    bannerMode: member.banner_mode,
    bannerSecondaryColor: member.banner_secondary_color,
    status: member.presence,
    bio: null,
    roles: member.roles.map((role) => ({ id: role.xid, label: role.name, color: role.color })),
    mutualSpaces: [space.name],
    badges: member.badges.filter((badge): badge is BadgeId => badge in BADGES),
    communityTag: member.community_tag,
    memberSince: new Date(member.joined_at).toLocaleDateString(),
    currentSpaceXid: space.xid,
  };
}

function mentionedAccounts(body: string, members: SpaceMember[]): string[] {
  const mentionsEveryone = /(^|\s)@everyone(?=\s|$|[.,!?;:])/i.test(body);
  const mentionsHere = /(^|\s)@here(?=\s|$|[.,!?;:])/i.test(body);
  return members
    .filter((member) => {
      if (mentionsEveryone) return true;
      if (mentionsHere && member.presence !== "offline") return true;
      const escaped = member.username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return new RegExp(`(^|\\s)@${escaped}(?=\\s|$|[.,!?;:])`, "i").test(body);
    })
    .map((member) => member.account_id);
}
